import { useEffect, useState } from "react";
import RoundRockHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Round Rock?", a: "We offer Round Rock-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The exact timeline depends on your application type, document completeness, and current State Department processing volumes." },
  { q: "Do I need to visit an office in Round Rock?", a: "No. Our service is remote: you complete forms online, visit a local acceptance facility (like a Round Rock post office or Williamson County clerk) for in-person verification if needed, then ship documents to us via overnight courier." },
  { q: "Where is the nearest passport acceptance facility in Round Rock?", a: "Round Rock has acceptance facilities at the Round Rock Main Post Office, South Round Rock Station, and Williamson County Clerk offices. Cedar Park, Pflugerville, and Georgetown post offices also serve the area. We provide a curated list with hours and contact information." },
  { q: "Is Austin-Bergstrom International Airport close to Round Rock?", a: "Yes — AUS is approximately 25 miles south of Round Rock. Round Rock residents are frequent Austin-Bergstrom international travelers, flying on Southwest, American, Delta, and United to international destinations. Passport urgencies discovered at AUS are far more stressful than at home — let us handle it before you get to the airport." },
  { q: "How much does expedited passport service cost in Round Rock?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee covering document review, overnight shipping, and expedited submission. We offer a best price guarantee and rates typically 30–100% lower than third-party resellers." },
  { q: "What are the main reasons Round Rock residents need expedited passports?", a: "Dell Technologies business travel, spring break family trips, Galveston cruise departures, Austin Community College study abroad, and tech startup international expansion travel are the most common reasons. Round Rock's explosive population growth means many recent transplants have never used a Round Rock acceptance facility." },
  { q: "What documents do Round Rock applicants need?", a: "For a new adult passport: proof of citizenship, government-issued photo ID, DS-11 form, and passport photo. For renewals: your most recent passport, one photo, and DS-82 form. For minors: both parents or notarized consent, birth certificate, and photos." },
  { q: "Can I get a passport photo in Round Rock?", a: "Yes. Several Round Rock CVS, Walgreens, and UPS Store locations offer passport photos. We recommend confirming State Department photo standard compliance before visiting." },
  { q: "How fast is Round Rock growing and why does it matter for passports?", a: "Round Rock is one of the fastest-growing cities in the United States. Many newcomers from other states are not familiar with local acceptance facilities and may not know where to start the passport application process. We guide Round Rock residents through the entire process." },
  { q: "Can I track my Round Rock passport application?", a: "Yes. Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov." },
];

function RoundRockStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) { setActive(id); break; }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" }, { id: "services", label: "Services" }, { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" }, { id: "destinations", label: "Destinations" }, { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" }, { id: "comparison", label: "Compare" }, { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" }, { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (<a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>{l.label}</a>))}
      </div>
    </div>
  );
}

function RoundRockProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Round Rock Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">We offer multiple speed tiers for Round Rock and Williamson County travelers. Choose the timeline that matches your departure urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Round Rock travelers departing AUS within 3 days. Full document review and overnight courier submission." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For Dell and tech industry travelers with business trips in 1–2 weeks. Best value for most Round Rock clients." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Williamson County families planning ahead. Significantly faster than standard post office processing (10–13 weeks)." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoundRockServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Round Rock travelers with imminent AUS departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Round Rock residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for Round Rock adults with acceptance facility support." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for Williamson County residents." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Round Rock and Pflugerville families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-stack-line", title: "Second Passport", desc: "Second passport for frequent Round Rock tech and business travelers with overlapping trips." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors before State Department submission." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Round Rock Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Complete passport solutions for Round Rock's fast-growing community — from Dell employees to young families to ACC students — all flying through Austin-Bergstrom.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoundRockLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Round Rock</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Round Rock is the fastest-growing city in the Austin metro area and one of the fastest-growing in the United States. With a population approaching 140,000 and growth rates that have consistently ranked it among the top 10 fastest-growing cities in America, Round Rock has transformed from a quiet Austin suburb into a thriving mid-sized city in its own right. Dell Technologies' global headquarters sits within the city limits, anchoring an employer ecosystem that draws tech talent from across the country and around the world.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Dell Technologies is Round Rock's dominant employer, with thousands of employees working on global supply chain, enterprise sales, and technology development projects that require international travel. Dell employees travel regularly to manufacturing facilities in China, Ireland, Malaysia, and Brazil; to data center deployments in Europe; and to customer sites across Latin America and the Middle East. These trips are often communicated with 1–3 weeks of notice, creating a constant stream of passport urgency in Round Rock's professional community.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Round Rock's explosive growth has attracted transplants from California, the Northeast, and the Pacific Northwest — many of whom arrived with valid passports that have since expired, or who have had children in Texas who have never had passports. The city's population skews young (median age in the low 30s) with young families who may be booking their first international family vacation and discovering that their children need first-time applications.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The Round Rock and Williamson County area also includes significant healthcare and biotech employment. Seton Medical Center Williamson, St. David's Georgetown Hospital, and several pharmaceutical and biotech companies headquartered in the area send medical professionals and researchers to international conferences and clinical trial sites in Europe and Asia. Medical conference travel often has fixed, non-reschedulable presentation dates — making passport processing urgency an occupational hazard for healthcare researchers.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            Austin Community College serves a significant population of Round Rock residents through its Round Rock campus. ACC has growing study abroad programs and sends students to Europe, Latin America, and Asia each year. Many ACC students — particularly first-generation college students — have never had passports. Program acceptance-to-orientation windows are often 4–8 weeks, requiring expedited processing to meet start dates.
          </p>
        </div>
      </div>
    </section>
  );
}

function RoundRockTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Round Rock Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">AUS — Austin-Bergstrom International Airport</h3>
                <p className="text-gray-400 text-xs">~25 miles south of Round Rock, rapidly expanding international service</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Austin-Bergstrom has undergone massive international expansion, adding direct routes to London Heathrow, Frankfurt, Amsterdam, Cancun, Mexico City, and other international destinations. Round Rock residents are among the heaviest AUS users in the metro area. The airport's rapid expansion means international route options keep growing — and so do the passport urgencies when Round Rock travelers realize their documents are expired or children have never had passports.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> AUS has added several new international routes in recent years. Many Round Rock travelers book these new routes immediately on launch — and then discover their passport expired years ago. Check passport validity the moment you book.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-computer-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Dell Technologies &amp; Tech Corridor</h3>
                <p className="text-gray-400 text-xs">Dell global HQ, Austin tech ecosystem, Round Rock employer belt</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Dell Technologies' Round Rock headquarters employs thousands of people who travel internationally on short notice. Dell supply chain managers fly to China and Malaysia for manufacturing partner meetings. Enterprise sales teams travel to Europe for major account meetings. IT consultants deploy to data centers worldwide for installation projects. These assignments arrive with 7–14 days of runway, creating exactly the kind of urgent passport demand that our courier service handles every week.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Dell and other tech companies book employees on international trips through corporate travel desks that do not flag passport expiration. Always self-check your passport, especially for Asia Pacific routes where 6-month validity is required.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Galveston Cruise Departures</h3>
                <p className="text-gray-400 text-xs">~3 hours south on I-35 and I-10</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Round Rock families regularly make the 3-hour drive to Galveston for Royal Caribbean, Carnival, and Disney cruises. The Williamson County family demographic — young families with children 5–15 — is exactly the profile that books Galveston cruises and discovers passport issues. Children who have never traveled internationally may have no passport at all, while parents' passports may have expired during COVID years when international travel stopped.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> With 3 hours to Galveston and a cruise sailing date, there is very little time to spare if you discover a passport problem after booking. Let us process everything before you point the car south.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-hospital-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Healthcare &amp; Biotech Industry Travel</h3>
                <p className="text-gray-400 text-xs">Seton, St. David's, pharmaceutical and biotech companies in Williamson County</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Williamson County has a growing healthcare and biotech employer base. Medical professionals from Round Rock hospitals travel to international medical conferences in Europe and Asia with fixed presentation dates. Pharmaceutical company employees travel to manufacturing facilities and clinical trial sites abroad. Healthcare researchers attending the European Society of Cardiology or ASCO annual meetings cannot reschedule their presentations — passport issues must be resolved before they arrive at AUS.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> International medical conference presentations cannot be rescheduled. Start the passport process the moment you receive your abstract acceptance — not the week of the conference.</p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-community-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Round Rock's Transplant Population &amp; Passport Urgency</h3>
            <p className="text-white/60 text-sm leading-relaxed">Round Rock's explosive growth has brought tens of thousands of transplants from California, New York, and other states — many of whom have children born in Texas who have never had passports, or parents whose passports expired years ago. We regularly help Round Rock newcomers navigate their first Texas passport experience.</p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function RoundRockDestinationsSection() {
  const destinations = [
    { name: "Ireland & Europe", context: "Dell Technologies Dublin operations, tech company European headquarters, AUS-London direct flights", urgency: "Corporate tech travel often booked 2–3 weeks out with fixed meeting dates", icon: "ri-map-pin-2-line" },
    { name: "China & Malaysia", context: "Dell supply chain manufacturing visits, component factory inspections, supplier partner meetings", urgency: "Manufacturing partner meetings and factory inspections often arrive with 7–14 days' notice", icon: "ri-map-pin-2-line" },
    { name: "Mexico & Caribbean", context: "AUS-Cancun direct flights, Galveston cruise departures, family vacation packages", urgency: "Spring break AUS-Cancun bookings peak January through March with 2–4 week lead times", icon: "ri-map-pin-2-line" },
    { name: "United Kingdom", context: "AUS-London Heathrow direct, corporate tech meetings, heritage travel for Round Rock transplants", urgency: "AUS-LHR direct flights frequently booked with short lead times for corporate travel", icon: "ri-map-pin-2-line" },
    { name: "India", context: "Round Rock's Indian-American tech worker community, family visits, IT business travel", urgency: "Family wedding season travel peaks October–February with 4–6 week booking windows", icon: "ri-map-pin-2-line" },
    { name: "Central America & Panama", context: "AUS international connections, adoption travel, medical mission trips from Round Rock churches", urgency: "Adoption travel and mission trip dates are fixed and cannot be rescheduled", icon: "ri-map-pin-2-line" },
  ];
  const stats = [
    { stat: "#1", label: "Round Rock consistently ranks among the fastest-growing cities in the United States" },
    { stat: "25 Miles", label: "Round Rock's distance from Austin-Bergstrom International Airport" },
    { stat: "Dell HQ", label: "Dell Technologies global headquarters, Round Rock's largest employer, drives tech international travel" },
    { stat: "48 Hours", label: "Fastest turnaround we've accommodated for a Dell employee deploying to Dublin" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Round Rock Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Austin-Metro Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across Central Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/austin" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Austin <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/killeen" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Killeen <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/waco" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Waco <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RoundRockTestimonialsSection() {
  const testimonials = [
    { name: "Jason T.", location: "Dell HQ, Round Rock", text: "My manager booked me to Dublin in 10 days and my passport expired two months ago. Fast Passport Center processed it in 48 hours. I made the trip and closed the deal.", rating: 5 },
    { name: "The Hernandez Family", location: "Round Rock", text: "We booked a Galveston Disney cruise and realized our twins had never had passports. They processed both applications in 6 days. Our family cruise was perfect.", rating: 5 },
    { name: "Dr. Kim", location: "Seton Medical, Round Rock", text: "I had a presentation at a conference in Amsterdam in 12 days and my passport was expired. Fast Passport Center processed the renewal in 5 days. Saved my conference slot.", rating: 5 },
    { name: "Maria L.", location: "Pflugerville/Round Rock", text: "I'm an ACC student and got selected for a study abroad semester in Spain. They guided me through my first passport application and got it back in 7 days. Made my program orientation.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Round Rock Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">{Array.from({ length: t.rating }).map((_, j) => (<i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>))}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoundRockAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "Round Rock Main Post Office", address: "301 W Anderson Ave, Round Rock, TX 78664", phone: "(512) 255-5028", photo: "Yes", appointment: "Required" },
    { name: "South Round Rock Station", address: "2001 N Mays St, Round Rock, TX 78664", phone: "(512) 828-3590", photo: "Yes", appointment: "Walk-in" },
    { name: "Williamson County Clerk", address: "405 Martin Luther King St, Georgetown, TX 78626", phone: "(512) 943-1515", photo: "No", appointment: "Required" },
    { name: "Cedar Park Post Office", address: "1001 N Bell Blvd, Cedar Park, TX 78613", phone: "(512) 335-2244", photo: "Yes", appointment: "Required" },
    { name: "Pflugerville Post Office", address: "15305 FM 1825, Pflugerville, TX 78660", phone: "(512) 670-5303", photo: "Yes", appointment: "Walk-in" },
    { name: "Georgetown Post Office", address: "214 E 9th St, Georgetown, TX 78626", phone: "(512) 863-2694", photo: "Yes", appointment: "Required" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Round Rock Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility. Here are Williamson County locations with contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. Call ahead to confirm availability. We can help find the earliest available slot across all Williamson County facilities.</p>
        </div>
      </div>
    </section>
  );
}

function RoundRockComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Round Rock Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Third-Party Resellers</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "7–14 days" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "Minimal or extra fee" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Often 30–100% markup" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Reseller, not registered" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "Often limited" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", reseller: "Varies" },
                { feature: "Round Rock Guidance", us: "Yes, local facility knowledge", post: "Generic", reseller: "Generic" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "Varies" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function RoundRockMistakesSection() {
  const mistakes = [
    { title: "COVID-Era Expired Passports", desc: "Thousands of Round Rock residents got passports for travel they canceled during COVID — and then never renewed. Many don't realize their passports expired until they book their first post-COVID international trip." },
    { title: "Children Who Have Never Had Passports", desc: "Round Rock's young family demographic includes many children who were born during COVID years and have never had passports. First-time applications require in-person visits — we guide families through the process from start to finish." },
    { title: "Not Checking Validity for Asia Pacific", desc: "Dell employees traveling to China, Malaysia, and Japan often don't know these countries require 6 months of passport validity beyond the return date. We flag this before submission." },
    { title: "Assuming Austin Has an Easy Passport Agency", desc: "Austin does not have a dedicated passport agency. There is no walk-in option. Round Rock residents who discover an expired passport the week of their AUS departure have limited options — our courier service is the fastest available." },
    { title: "Transplant Confusion on Texas Facilities", desc: "Round Rock's many transplants from other states often don't know which local post offices offer passport acceptance services. We maintain an updated facility list and can help find the earliest available appointment." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Round Rock Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoundRockHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a Round Rock-area acceptance facility. We find the nearest one with the earliest appointment." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed package overnight to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit through our registered State Department courier channel. Full tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Delivered to your Round Rock address via secure overnight shipping within your chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Round Rock Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoundRockFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Round Rock Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoundRockFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Round Rock Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">Whether you are flying from AUS in 3 days or sailing from Galveston in 3 weeks, we will get your passport processed on time. A+ BBB rated. Registered State Dept. courier.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function RoundRockPage() {
  useEffect(() => {
    document.title = "Emergency Passport Round Rock, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/round-rock/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Round Rock, TX — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Dell Technologies employees, Austin metro families, Williamson County residents, and ACC students flying through Austin-Bergstrom.");
    setMeta("geo.placename", "Round Rock, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "30.5082;-97.6789");
    setMeta("ICBM", "30.5082, -97.6789");
    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <RoundRockHeroSection />
      <RoundRockStickyNav />
      <RoundRockProcessingTimeSection />
      <RoundRockServicesSection />
      <RoundRockLocalAreaSection />
      <RoundRockTravelTimingSection />
      <RoundRockDestinationsSection />
      <RoundRockTestimonialsSection />
      <RoundRockAcceptanceFacilitiesSection />
      <RoundRockComparisonSection />
      <RoundRockMistakesSection />
      <RoundRockHowItWorksSection />
      <RoundRockFAQSection />
      <SEOCrossLinks currentRoute="/cities/round-rock" state="TX" />
      <RoundRockFinalCTA />
    </div>
  );
}